import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { UtilsService } from '../utils/utils.service';
import { BehaviorSubject } from 'rxjs';

/** Selected rating question for Trends (primary + is_displayed_on_summary), project scope only */
export interface TrendsSummaryQuestionSelection {
  fieldId: string;
  name: string;
  main_rater_type?: number;
  scale_rater_type?: string;
}
import * as moment from 'moment';
import { DownloadService } from '../downloads/download.service';
import { ToastService } from '../toast/toast.service';
import { Papa } from 'ngx-papaparse';
import * as _ from 'lodash';
@Injectable({
  providedIn: 'root',
})

export class ResponseAnalyticsService {
  private ratingInfoSource = new BehaviorSubject<any>({});
  ratingInfo = this.ratingInfoSource.asObservable();
  private tabSwitchSource = new BehaviorSubject<any>({});
  tabSwitch = this.tabSwitchSource.asObservable();
  private scrollTriggerSource = new BehaviorSubject<any>({});
  scrollTrigger = this.scrollTriggerSource.asObservable();
  private trendsSummaryQuestionSource = new BehaviorSubject<TrendsSummaryQuestionSelection | null>(null);
  trendsSummaryQuestionSelection = this.trendsSummaryQuestionSource.asObservable();


  constructor(public _utilService: UtilsService, private downloadService: DownloadService, private toastService: ToastService, private papa: Papa,) { }

  getAccountScoresSummary(reqBody: any): any {
    try {
      const url = this._utilService.apiUrlGeneration(environment.analyticsV1, environment.analyticsV1.AccountScoresSummary);
      return this._utilService.postAPICall(url, reqBody, this._utilService.setHeaders());
    } catch (error) {
      return 'An unexpected error occurred while getting summary data.';
    }
  }

  getcomparisonV2Data(reqBody: any): any {
    try {
      const url = this._utilService.apiUrlGeneration(environment.analyticsV1, environment.analyticsV1.comparisonV2Data);
      return this._utilService.postAPICall(url, reqBody, this._utilService.setHeaders());
    } catch (error) {
      return 'An unexpected error occurred while getting summary data.';
    }
  }

  getTrendDataRatingSummary(reqBody: any): any {
    try {
      const url = this._utilService.apiUrlGeneration(environment.analyticsV1, environment.analyticsV1.trendDataRatingSummary);
      return this._utilService.postAPICall(url, reqBody, this._utilService.setHeaders());
    } catch (error) {
      return 'An unexpected error occurred while getting summary data.';
    }
  }

  getoverallRating(reqBody: any): any {
    try {
      const url = this._utilService.apiUrlGeneration(environment.analyticsV1, environment.analyticsV1.overallRating);
      return this._utilService.postAPICall(url, reqBody, this._utilService.setHeaders());
    } catch (error) {
      return 'An unexpected error occurred while getting summary data.';
    }
  }

  getcrossTabAnalytics(reqBody: any): any {
    try {
      const url = this._utilService.apiUrlGeneration(environment.analyticsV1, environment.analyticsV1.crossTabAnalytics);
      return this._utilService.postAPICall(url, reqBody, this._utilService.setHeaders());
    } catch (error) {
      return 'An unexpected error occurred while getting summary data.';
    }
  }

  getScoreAccount(reqBody: any): any {
    try {
      const url = this._utilService.apiUrlGeneration(environment.analyticsV1, environment.analyticsV1.scoreAccount);
      return this._utilService.postAPICall(url, reqBody, this._utilService.setHeaders());
    } catch (error) {
      return 'An unexpected error occurred while getting summary data.';
    }
  }

  getWordCloud(reqBody: any): any {
    try {
      const url = this._utilService.apiUrlGeneration(environment.analyticsV1, environment.analyticsV1.wordcloud);
      return this._utilService.postAPICall(url, reqBody, this._utilService.setHeaders());
    } catch (error) {
      return 'An unexpected error occurred while getting summary data.';
    }
  }

  switchTab(tab: string): void {
    this.tabSwitchSource.next({ tab });
  }

  setRatingInfo(scalerFieldId: number): void {
    const currentState: any = {
      scalerType: scalerFieldId,
      cardName: scalerFieldId === 3 ? 'NPS' : 'Rating',
      columnName: scalerFieldId === 3 ? 'NPS' : 'Score'
    };
    const ratingLabels: any = {
      1: { top: 'Positive', bottom: 'Negative' },
      3: { top: 'Promoters', middle: 'Passives', bottom: 'Detractors' },
      4: { top: 'T2B', middle: 'MB', bottom: 'B2B' },
      6: { top: 'T3B', middle: 'MB', bottom: 'B3B' },
      7: { top: 'T2B', middle: 'MB', bottom: 'B2B' },
      8: { top: 'Positive', bottom: 'Negative' },
      9: { top: 'TB', middle: 'MB', bottom: 'BB' }
    };
    Object.assign(currentState, ratingLabels[scalerFieldId] || {});
    this.ratingInfoSource.next(currentState);
  }

  formatNumber(number: number): string {
    const isNegative = number < 0;
    number = Math.abs(number);
    const [integerPart, decimalPart] = number.toString().split('.');
    if (integerPart.length < 4) {
      return (isNegative ? '-' : '') + integerPart + (decimalPart ? '.' + decimalPart : '');
    }
    const lastThreeDigits = integerPart.slice(-3);
    const formattedInteger = (integerPart.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThreeDigits).replace(/^,/, '');
    return (isNegative ? '-' : '') + formattedInteger + (decimalPart ? '.' + decimalPart : '');
  }

  segregateData(response: any): any {
    return {
      responses: response?.doc_count || 0,
      top: response?.promoters || response?.top || response?.positives || 0,
      mid: response?.passives || response?.mid || 0,
      bottom: response?.detractors || response?.bottom || response?.negatives || 0,
      rating: response?.average_rating || 0
    };
  }

  segregateDataOnPercentage(response: any): any {
    const docCount = response?.doc_count || 0;
    const formatValue = (value: number) => {
      return Number.isInteger(value) ? value.toString() : value.toFixed(1);
    };
    const getValue = (keys: string[]) => {
      if (docCount === 0) {
        return '0';
      }
      const value = keys.reduce((acc, key) => acc || response?.[key] || 0, 0) / docCount * 100;
      return formatValue(value);
    };
    return [
      getValue(['promoters', 'top', 'positives']),
      getValue(['passives', 'mid']),
      getValue(['detractors', 'bottom', 'negatives'])
    ];
  }

  formattedTime(startDate: string, selectedTrend: number): string {
    const startDateString = moment(startDate, 'YYYYMMDD');
    switch (selectedTrend) {
      case 1:
        return startDateString.subtract(1, 'day').format("DD MMM'YY");
      case 2:
        return 'Last week';
      case 3:
        return startDateString.subtract(1, 'month').format("MMM'YY");
      case 4:
        return `${startDateString.subtract(3, 'month').format("MMM'YY")} - ${startDateString.add(2, 'month').format("MMM'YY")}`;
      case 5: {
        const yearStart = startDateString.subtract(1, 'year');
        return startDateString.format('MMM') === 'Jan' ? yearStart.format('YYYY') : `${yearStart.format("MMM YYYY")} - ${startDateString.add(1, 'year').subtract(1, 'month').format("MMM YYYY")}`;
      }
      default:
        return '';
    }
  }

  calculateNPS(docCount: number, topValue: number, bottomValue: number): string {
    return String(docCount > 0 ? (((topValue / docCount) * 100) - ((bottomValue / docCount) * 100)).toFixed(1) : 0);
  }

  setScrollTrigger(trigger: string): void {
    this.scrollTriggerSource.next(trigger);
  }

  /** Emits when user picks a summary rating question on Trends; null resets to default handling */
  setTrendsSummaryQuestionSelection(selection: TrendsSummaryQuestionSelection | null): void {
    this.trendsSummaryQuestionSource.next(selection);
  }

  getTrendsSummaryQuestionSnapshot(): TrendsSummaryQuestionSelection | null {
    return this.trendsSummaryQuestionSource.getValue();
  }

  // Main rater type / Scaler rater type ratings distribution for calculations
  getRatingBandsByRaterType(scaleRaterType: any): any {
    let mainRaterTypeRatingsDistribution = {
      '-5 to 5': {bottom: [-5, -4, -3, -2, -1], mid: [0], top: [1, 2, 3, 4, 5]},
      '-5 to +5': {bottom: [-5, -4, -3, -2, -1], mid: [0], top: [1, 2, 3, 4, 5]},
      '0-10': {bottom: [0, 1, 2, 3, 4, 5, 6], mid: [7, 8], top: [9, 10]},
      '1-5': {bottom: [0, 1, 2, 3, 4, 5, 6], mid: [7, 8], top: [9, 10]},
      '5': {bottom: [1, 2], mid: [3], top: [4, 5]},
      '1-10': {bottom: [1, 2, 3], mid: [4, 5, 6, 7], top: [8, 9, 10]},
      '7': {bottom: [1, 2], mid: [3, 4], top: [5, 6]},
      '8': {bottom: [-3, -2, -1], top: [1, 2, 3]},
      '9': {bottom: [1], mid: [2, 3], top: [4]}
    };
    return mainRaterTypeRatingsDistribution[scaleRaterType];
  }

  createDashboardReportingJob(downloadQuery: any) {
    this.downloadService.createDashboardReportingJob(downloadQuery).subscribe((reponse: any) => {
      if (reponse && reponse.litmus_job) {
        this.downloadService.updateList(reponse.litmus_job)
        this.downloadService.showDownloadSideBar(true);
      } else if (reponse && reponse.code == 4021) {
        this.toastService.showPopup("WARN", "Please Wait", "Report generation is in process.", 3500);
      } else {
        this.toastService.showPopup("ERROR", "Error", "Error while generating request.", 3500);
      }
    })
  }

  getAccountCrossTab(query_object: any, remove_universal_filter?: any) {
    if (!_.isEmpty(Helpers.universalFilter) && !remove_universal_filter) {
      query_object.universal_filter = Helpers.getUniversalFilter();
    }
    const url = this._utilService.apiUrlGeneration(environment.analyticsV1, environment.analyticsV1.accountCrossTab);

    return this._utilService.postAPICall(url, query_object, this._utilService.setHeaders())
    // var response = $resource($rootScope.Helpers.getApiUrlV2('v1/litmus_analytics/score/account_cross_tab'), {}, {
    //     post: {
    //         method: 'POST'
    //     }
    // }).post(query_object, function(obj) {
    //     if (obj.data && obj.data.code == 4053) {
    //         Helpers.redirectToLogin();
    //     } else {
    //         cb(obj.data);
    //     }
    // });
  }

  prepareAndDownloadData(config: any) {
    if (_.has(config, 'headerKey') && _.has(config, 'dataKey') && _.has(config, 'data') && !_.isEmpty(config.data)) {
      let dataToDownload: any = Helpers.deepClone(config.data);
      let isSequnceEnabled: boolean = false;
      dataToDownload = _.map(dataToDownload, (dataRow: any) => {
        dataRow.columns = _.map(dataRow.columns, (column: any) => {
          if (column && column[config.headerKey]) {
            column.header = column[config.headerKey]
          }
          if (column && column[config.dataKey]) {
            column.dataKey = column[config.dataKey]
          }
          if (column && column[config.sequenceKey]) {
            column.sequence = column[config.sequenceKey]
            isSequnceEnabled = true;
          }
          return column;
        })
        return dataRow;
      })
      let fileData = this.getFileDetails(config.fileDetails);
      let CSV = fileData.CSV
      _.each(dataToDownload, (object: any) => {
        var dataObjects: any = this.prepareCsvData(object.columns, object.rows, config, isSequnceEnabled);
        const levels = Object.keys(dataObjects);
        // console.log("dataobject",dataObjects)
        if (levels && levels.length) {
          _.each(levels, (level: any) => {
            if (dataObjects && dataObjects[level] && dataObjects[level].length) {
              _.each(dataObjects[level], (jsonData: any) => {
                if (jsonData && jsonData.data && jsonData.data.length) {
                  jsonData.header = true;
                  CSV += this.papa.unparse(jsonData);
                  CSV += '\n\n';
                }
              })
            }
          })
        }

      })
      // //Initialize file format you want csv or xls
      var uri = 'data:text/csv;charset=utf-8,%EF%BB%BF' + encodeURIComponent(CSV);

      const downloadData = {
        _id: this.downloadService.getDownloadId(),
        filename: fileData.fileName,
        created_date: new Date().toISOString(),
        orignal_filename: fileData.fileName + ".csv",
        fileType: 'csv',
        dataSource: 'dashboard',
        progress: 100,
        uri: uri
      };
      this.downloadService.updateList(downloadData);
      this.downloadService.showDownloadSideBar(true);
    } else {
      console.log("incomplete parameter passed to dowload data.")
    }
  }

  isValidDownloadData(data: any) {
    let isValid: boolean = false;
    if (data && data.length) {
      let count = 0
      _.each(data, (dataRow: any) => {
        if (_.has(dataRow, 'columns') && !_.isEmpty(dataRow.columns)) {

          count = count + 1;
        }
      })
      isValid = (count == data.length) ? true : false;
    }
    return isValid;
  }


  getFileDetails(fileDetails: any) {
    fileDetails = (!fileDetails || _.isEmpty(fileDetails)) ? {} : fileDetails;
    var ReportTitle = fileDetails?.title || '';
    var fileName = fileDetails?.fileName || 'Report';
    fileName += ' ' + this.getDateValue();
    var CSV = '';
    // //Set Report title in first row or line

    CSV += 'Report name :,"' + ReportTitle + '"\r';

    if (!fileDetails.reportDate) {
      CSV += 'Report Date :, "' + this.getFormatedReportDate(Helpers, 'startDate', 'DD-MMM-YYYY') + ' to ' + this.getFormatedReportDate(Helpers, 'endDate', 'DD-MMM-YYYY') + '"\r';
    } else {
      CSV += 'Report Date :, "' + fileDetails.reportDate + '"\r';
    }


    if (fileDetails && fileDetails.accountName && fileDetails.accountName != '') {
      CSV += 'For Account :, "' + fileDetails.accountName + '"\r';
    }

    if (fileDetails && fileDetails.brandName && fileDetails.brandName != '') {
      CSV += 'For Project :, "' + fileDetails.brandName + '"\r';
    }

    if (fileDetails && fileDetails.interaction && fileDetails.interaction != '') {
      CSV += 'Interaction :, "' + fileDetails.interaction + '"\r';
    }


    CSV += 'Downloaded On :,"' + moment().format('DD-MMM-YYYY hh:mm:ss a') + '"\r';

    // // User Details
    var userResponse = _.get(Helpers, 'managerDetails', '');
    if (userResponse && _.hasIn(userResponse, 'manager.name')) {
      CSV += 'Downloaded By :, "' + userResponse.manager.name + '"\r';
    }

    CSV += '\n\n';
    return { CSV, fileName }

  }

  getFormatedReportDate(object: any, key: string, format: string) {
    const date = _.get(object, 'datePicker.' + key, '');
    if (!_.isEmpty(date)) {
      return moment(date, 'YYYYMMDD').format(format);
    }
    return date;

  }
  getDateValue() {
    let currentDate = new Date();
    return moment(currentDate).format('DD_MMM_YYYY');
  }


  prepareCsvData(columns: any, data: any, config: any, isSequnceEnabled: boolean) {
    let drilldownCSV: any = {}

    columns = _.map(columns, (column: any) => {
      if (column && column[config.headerKey]) {
        column.header = column[config.headerKey]
      }
      if (column && column[config.dataKey]) {
        column.dataKey = column[config.dataKey]
      }
      if (column && column[config.sequenceKey]) {
        column.sequence = column[config.sequenceKey]
        isSequnceEnabled = true;
      }
      return column;
    })

    if (isSequnceEnabled) {
      columns = _.orderBy(columns, ['sequence'], ['asc']);
    }
    let level: any = 1;
    drilldownCSV[level] = []

    let csvData: any = {
      fields: _.map(columns, (column: any) => column['header']),
      data: []
    }

    _.each(data, (row: any) => {
      let temp: any = [];
      _.each(columns, (column: any) => {
        const value = _.get(row, column.dataKey, '');
        temp.push(value);
      })

      row.level = level;
      if (row && row.drillDown && row.drillDown.length) {
        row.drilldown = row.drillDown        
      }
      if (row && row.drilldown && row.drilldown.length) {
        drilldownCSV = this.prepareChildCSVData(drilldownCSV, columns, row, config, isSequnceEnabled)
      }
      csvData.data.push(temp);
    });
    drilldownCSV[level].push(csvData)
    return drilldownCSV;
  }

  prepareChildCSVData(drilldownCSV: any, columns: any, data: any, config: any, isSequnceEnabled: boolean) {
    let level = data.level + 1;
    let childColumns: any = [];
    if (data.header && data.fixedHeader) {
      childColumns = [...data.fixedHeader, ...data.header]
    } else if (data.header) {
      childColumns = data.header
    } else if (data.fixedHeader) {
      childColumns = data.fixedHeader
    } else if (data.columns) {
      childColumns = data.columns
    } else {
      childColumns = columns
    }
    if (drilldownCSV && !drilldownCSV[level]) {
      drilldownCSV[level] = []
    }
    childColumns = _.map(childColumns, (column: any) => {
      if (config && config.headerKey && column && column[config.headerKey]) {
        column.header = column[config.headerKey]
      }
      if (config && config.dataKey && column && column[config.dataKey]) {
        column.dataKey = column[config.dataKey]
      }
      if (config.sequenceKey && config.sequenceKey && column && column[config.sequenceKey]) {
        column.sequence = column[config.sequenceKey]
      }
      return column;
    })

    if (isSequnceEnabled) {
      childColumns = _.orderBy(childColumns, ['sequence'], ['asc']);
    }

    let tempCsvData: any = {
      fields: _.map(childColumns, (column: any) => column['header']),
      data: []
    }

    _.each(data.drilldown, (row: any) => {
      let temp: any = [];
      _.each(childColumns, (column: any) => {
        const value = _.get(row, column.dataKey, '');
        temp.push(value);
      })
      row.level = level;
      if (row && row.drillDown && row.drillDown.length) {
        row.drilldown = row.drillDown        
      }
      if (row && row.drilldown && row.drilldown.length) {
        this.prepareChildCSVData(drilldownCSV, childColumns, row, config, isSequnceEnabled)
      }
      tempCsvData.data.push(temp);
    })
    drilldownCSV[level].push(tempCsvData)
    return drilldownCSV;
  }
}